<template>
  <v-dialog v-model="dialog" persistent max-width="950px">
    <v-card>
      <v-card-title>
        <span class="headline">{{
            item ? `Edit Asset(${item.name})` : (this.availableId ? `New Asset(${this.availableId})` : 'New Asset') }} </span>
        <v-spacer></v-spacer>
        <v-icon @click="closeDialog">mdi-close</v-icon>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field
                  v-model="assetData.name"
                  :rules="validationRules.name"
                  label="Name"
                  placeholder="The asset name(be descriptive)"
                  persistent-placeholder
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                  v-model="assetData.type"
                  :items="assetTypes"
                  label="Type"
                  placeholder="Select the asset type"
                  persistent-placeholder
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                  v-model="assetData.affiliateId"
                  label="Access"
                  placeholder="Enter the affiliate ID or leave empty if public"
                  persistent-placeholder
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                  v-model="assetData.selectedTags"
                  :items="filteredTags"
                  multiple
                  chips
                  small-chips
                  :menu-props="{ maxHeight: '400' }"
                  class="custom-autocomplete"
                  label="Tags"
                  placeholder="Add and/or create tags"
                  persistent-placeholder
                  @change="updateSelectedTags"
                  @keyup.enter="addNewTags"
                  :search-input.sync="searchTags"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                      small
                      label
                      close
                      color="primary"
                      @click:close="removeTag(index)"
                  >
                    {{ item }}
                  </v-chip>
                </template>
                <template v-slot:no-data>
                  <span>Press enter to create new tag</span>
                </template>
              </v-autocomplete>

            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="assetData.type === 'background' || assetData.type === 'product_image'">
              <div class="image-upload-container">
                <input
                    type="file"
                    @change="handleFileChange"
                    accept="image/*"
                    style="display: none"
                    ref="fileInput"
                />
                <div @click="openFileInput" class="upload-area">
                  <div v-if="assetData.imageUrl">
                    <img v-if="assetData.imageUrl" :src="assetData.imageUrl" alt="Preview" class="image-preview">
                  </div>
                  <div v-else>
                    <div class="upload-instructions">
                      Drag & drop files or
                      <span class="browse-link">Browse</span>
                    </div>
                    <div class="supported-format">
                      Supported format: WEBP
                    </div>
                  </div>
                </div>

                <div class="file-upload-details" v-if="isUploading">
                  <span>Uploading: {{ assetData.imageFileName }}</span>
                  <progress-bar :progress="uploadProgress"></progress-bar>
                </div>
                <div class="error-message" v-if="uploadError">
                  <span>{{ uploadErrorMessage }}</span>
                </div>
                <div class="file-info">
                  <img v-if="assetData.imageUrl" :src="assetData.imageUrl" alt="Uploaded Image" class="uploaded-image-preview">
                  <div class="file-name">{{ assetData.imageFileName }}</div>
                  <div class="remove-file" @click="removeFile">×</div>
                </div>
                <v-btn :disabled="!isUploadValid" color="primary" @click="uploadImage">
                  UPLOAD FILE
                </v-btn>
                <div v-if="isUploading">Uploading...</div>
              </div>
            </v-col>
            <v-col cols="12" v-else-if="assetData.type === 'keyword' || assetData.type === 'product_name'">
              <div>
                <label class="text-caption font-weight-medium mb-1 d-block" style="color:#2196f3;">Text Value</label>
                <div class="d-flex align-center">
                  <v-icon
                      small
                      class="mr-2"
                      style="cursor: pointer;"
                      @click="applyAutoKeyword"
                  >mdi-auto-fix</v-icon>
                  <v-text-field
                      v-model="assetData.keywordField"
                      hide-details
                      dense
                      class="flex-grow-1"
                      placeholder="The text that will appear on the Advertiser landing page"
                  />
                </div>
              </div>


            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="!isFormValid" color="primary" @click="handleCreateOrUpdateAsset()">
            {{ isEdit ? 'UPDATE ASSET' : 'CREATE ASSET' }}
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>


<script>
import axios from "axios";
import customAssets from "@/api/customAssets";
import {mapActions, mapGetters, mapState} from "vuex";
import ProgressBar from "@/components/pages/affiliates/customAssets/ProgressBar.vue";

export default {
  components: {ProgressBar},
  props: {
    item:{},
    value: Boolean,
  },
  data() {
    return {
      isUploading: false,
      isEdit: false,
      uploadProgress: 0,
      uploadError: false,
      uploadErrorMessage: '',
      uploaded: false,
      isValidImage: true,
      searchTags:"",
      filteredTags: [],
      originAllTags: [],
      validationRules: {
        name: [(v) => !!v || 'Name is required'],
        type: [(v) => !!v || 'Type is required'],
        imageFileName: [(v) => !!v || 'Image file is required'],
        keywordField: [(v) => (this.assetData.type === 'keyword' ? !!v || 'Keyword text is required' : true)],
        imageUrl: [(v) => (this.assetData.type === 'background' ? !!v || 'Image URL is required' : true)],
        affiliateId: [],
      },
      selectedFile: null,
      dialog: this.value,
      assetData: {
        name: '',
        type: '',
        imageFileName: '',
        keywordField: '',
        imageUrl: '',
        uploadedToS3: false,
        affiliateId: '',
        assets: '',
        tags: [],
        selectedTags: [],
        selectedTagsObj: [],
        isEdit: false,
      },
      assetTypes: ['keyword', 'background', 'product_image', 'product_name'],
    };
  },

  computed: {
    ...mapGetters('customAssets', ['getCustomAssetsTags','availableId']),
    ...mapState('user', ['user']),
    isUploadValid(){
      return this.assetData.imageFileName;
    },
    isFormValid() {
      if (this.assetData.type === 'keyword' || this.assetData.type === 'product_name') {
        return this.assetData.name && this.assetData.keywordField;
      }
      if (this.assetData.type === 'background' || this.assetData.type === 'product_image') {
        return this.assetData.name && this.assetData.imageFileName && this.assetData.uploadedToS3;
      }
      return false; // Default case (shouldn't happen with proper type selection)
    },
  },
  methods: {
    ...mapActions('customAssets', [
      'getAvailableIdStore',
    ]),
    removeTag(index) {
      this.assetData.selectedTags.splice(index, 1);
    },
    updateSelectedTags() {
      // this.assetData.tags = this.assetData.tags.map((tag) => tag.name);
    },
    addNewTags() {
      if (this.searchTags && !this.assetData.tags.some(v => v.name === this.searchTags)) {
        this.assetData.selectedTags.push(this.searchTags);
        this.filteredTags.push(this.searchTags);
        this.searchTags = '';
      }
    },
    openFileInput() {
      this.$refs.fileInput.click();
      this.selectedFile = this.$refs.fileInput
    },
    removeFile() {
      this.uploaded = false;
      this.assetData.imageFileName = '';
      this.assetData.imageUrl = '';
    },
    handleFileChange(event) {
      this.selectedFile = event.target.files[0];

      const file = event.target.files[0];
      if (file) {
        this.assetData.imageFileName = file.name;
        this.uploadProgress = 0;
        this.uploadError = false;
        this.isUploading = true;

        const intervalId = setInterval(() => {
          if (this.uploadProgress >= 100) {
            clearInterval(intervalId);
            this.isUploading = false;
          } else {
            this.uploadProgress += 50;
          }
        }, 500);

      }
    },
    applyAutoKeyword() {
      if (this.assetData.type === 'keyword') {
        const name = this.assetData.name || '[NAME]';
        this.assetData.keywordField = `<sm>Access</sm>${name}<sm>Content and More</sm>`;
      }
    },
    async uploadImage() {
      this.isUploading = true;
      try {
        const fileNameWithoutExtension = this.selectedFile.name.slice(0, this.selectedFile.name.lastIndexOf('.'));

        const fileName = this.selectedFile.name;
        const fileExtension = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2);

        if(fileExtension !== 'webp'){
          alert('Please upload webp file only');
          return;
        }
        const id = this.assetData.id ? this.assetData.id : this.availableId;
        const presignedURL = await customAssets.customAssetsImageUploadUrl(id, 'BACKGROUND', fileExtension, 'PUT');
        await axios.put(presignedURL, this.selectedFile, {
          headers: {
            'Content-Type': this.selectedFile.type,
          },
        });

        const imageUrl = await customAssets.customAssetsImageUploadUrl(id, 'BACKGROUND', fileExtension, 'GET');
        this.assetData.imageUrl = imageUrl;
        this.assetData.uploadedToS3 = true;
        this.uploaded = true;

      } catch (e) {
        alert('Error uploading image')
        this.assetData.uploadedToS3 = false;
        console.error('Error uploading image', e);
      } finally {
        this.isUploading = false;
      }

    },
    handleCreateOrUpdateAsset() {
      this.assetData.isEdit = this.isEdit;
      const selectedTagsObject = [];

      this.assetData.selectedTags.forEach((selectedTag) => {
        const foundTag = this.originAllTags.find((tag) => {
          return tag.name === selectedTag;
        });

        if (foundTag) {
          selectedTagsObject.push({
            'id': foundTag.id,
            'name': selectedTag
          });
        } else {
          selectedTagsObject.push({
            'id': null,
            'name': selectedTag
          });
        }
      });

      this.assetData.selectedTagsObj = selectedTagsObject;
      if (!this.assetData.id){
        this.assetData.id = this.availableId
      }
// Set the param based on type
      this.assetData.param = this.assetData.type === 'keyword' ? 'kw' :
        this.assetData.type === 'background' ? 'bg' :
          this.assetData.type === 'product_image' ? 'pi' :
            this.assetData.type === 'product_name' ? 'pn' : '';
      // Set the value based on type
      this.assetData.value = (this.assetData.type === 'keyword' || this.assetData.type === 'product_name') ?
        this.assetData.keywordField : this.assetData.imageFileName;
      this.$emit('createOrUpdateAsset', this.assetData);
      this.closeDialog();
    },
    closeDialog() {
      this.dialog = false;
      this.assetData = {
        id: null,
        name: '',
        type: '',
        imageFileName: '',
        keywordField: '',
        imageUrl: '',
        assets: '',
        tags: [],
        selectedTags: [],
      };
      this.$emit('close');
    },
  },
  async mounted() {
    await this.getAvailableIdStore()
    const tags = await this.getCustomAssetsTags;
    this.originAllTags = await this.getCustomAssetsTags;
    if (this.item) {
      this.isEdit = true;
      this.assetData.id = this.item.id;
      this.assetData.name = this.item.name;
      this.assetData.type = this.item.type;
      this.assetData.param = this.item.param;
      if (this.item.type === 'background' || this.item.type === 'product_image') {
        this.assetData.imageFileName = this.item.value;
        const assetType = 'BACKGROUND';
        const imageUrl = await customAssets.customAssetsImageUploadUrl(this.item.id, assetType, 'webp', 'GET');
        this.assetData.imageUrl = imageUrl;
      } else if (this.item.type === 'keyword' || this.item.type === 'product_name') {
        this.assetData.keywordField = this.item.value;
      }
      this.assetData.tags = tags.map((tag) => tag.name);
      this.assetData.selectedTags = this.item.tags.map((tag) => tag.name);
      this.assetData.affiliateId = this.item.affiliateId;

    }
    this.filteredTags = tags.length > 0 ? tags.map((tag) => tag.name) : [];
    this.assetData.uploadedToS3 = false;
  },

};
</script>

<style scoped>
.image-preview {
  max-width: 100%;
  max-height: 250px;
}

.upload-area {
  /*background-image: url('~@/assets/upload_file.svg');*/
  background-repeat: no-repeat;
  background-position: top;
  background-size: 50%;
  width: 400px;
  height: 200px;
  padding: 16px;
  border-radius: 8px;
  background-color: #F5F5F5;
  border: 1px dashed #CCCCCC;
  color: #333;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: background-color 0.2s;
  cursor: pointer;
}

.upload-area:hover {
  background-color: #E9E9E9;
}

.image-preview {
  max-width: 100%;
  max-height: 150px;
  margin-top: 16px;
}

input[type="file"] {
  display: none;
}

.upload-instructions {
  font-size: 24px;
  color: #000;
  font-weight: bold;
  margin-bottom: 16px;

}
.browse-link {
  color: #FFA500;
  cursor: pointer;
}

.supported-format {
  font-size: 18px;
  color: #666;
  font-weight: 300;

}

.file-upload-details {
}
.error-message {
  color: red;
}

.file-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  background-color: #f4f4f4;
  border: 1px solid #e1e1e1;
  padding: 0.5rem;
}
.uploaded-image-preview {
  max-width: 50px;
  max-height: 50px;
  margin-right: 1rem;
}
.file-name {
  flex-grow: 1;
}
.remove-file {
  cursor: pointer;
  color: #ff0000;
  font-weight: bold;
}

</style>
